import type { PropsWithChildren } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '@accure/ui';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/solid';
import { ErrorBoundary } from '@sentry/react';

import { CurrentAssetProvider } from '@/components/current-asset-provider';
import { Link } from '@/components/ui/link';
import { PageUrlKey } from '@/entities/page-url-key';
import { TasksPage } from '@/task-manager/pages/tasks';
import { cls } from '@/utils/cls';

import { LogoSimple } from '../components/layout/logo-simple';
import { ErrorFallback } from '../components/ui/error-fallback';
import { useAuth } from '../hooks/use-auth';
import { useFeatureFlag } from '../hooks/use-feature-flag';
import { useMe } from '../hooks/use-me';
import { DetailsPage } from '../pages/assets/details';
import { ConfettiProvider } from '../providers/confetti';

export function ThirdPartyRoutes() {
  const featureFlag = useFeatureFlag();
  const me = useMe();
  return (
    <Switch>
      {featureFlag('taskmanager') ? (
        <Route
          exact
          path={`/${PageUrlKey.TASKS}`}
          component={function () {
            return (
              <ThirdPartyShell>
                <TasksPage />
              </ThirdPartyShell>
            );
          }}
        />
      ) : null}
      {featureFlag('details') ? (
        <Route
          path={
            me.config.type === 'mobility'
              ? `/vehicle/:siteId/${PageUrlKey.DETAILS}`
              : `/asset/:siteId/${PageUrlKey.DETAILS}`
          }
          component={function () {
            return (
              <ThirdPartyShell>
                <DetailsPage />
              </ThirdPartyShell>
            );
          }}
        />
      ) : null}
      <Redirect to={`/${PageUrlKey.TASKS}`} />
    </Switch>
  );
}

type ThirdPartyShellProps = PropsWithChildren<{}>;

function ThirdPartyShell({ children }: ThirdPartyShellProps) {
  return (
    <CurrentAssetProvider>
      <ConfettiProvider>
        <div className="grid h-screen w-screen grid-rows-[min-content_1fr] overflow-hidden">
          <ThirdPartyAppHeader className="border-zinc-950/5 bg-zinc-50" />
          <main className="grow-1 relative size-full overflow-auto">
            <ErrorBoundary fallback={ErrorFallback}>{children}</ErrorBoundary>
          </main>
        </div>
      </ConfettiProvider>
    </CurrentAssetProvider>
  );
}

type ThirdPartyAppHeaderProps = {
  className?: string;
};

export const ThirdPartyAppHeader = ({
  className,
}: ThirdPartyAppHeaderProps) => {
  const auth = useAuth();

  async function handleLogout() {
    await auth.logout(window.location.origin);
  }

  return (
    <Navbar className={cls('relative border-b px-4 py-1', className)}>
      <div className="z-50 mr-auto flex h-full max-w-32 shrink-0 items-center">
        <Link to="/">
          <LogoSimple className="block h-4" />
        </Link>
      </div>
      <h1 className="inset-x-0 grow self-end py-1.5 text-center text-lg font-medium lg:absolute">
        Tasks
      </h1>
      <NavbarSpacer />
      <NavbarSection>
        <NavbarItem onClick={handleLogout}>
          <ArrowLeftStartOnRectangleIcon />
          <span className="max-sm:hidden">Sign Out</span>
        </NavbarItem>
      </NavbarSection>
    </Navbar>
  );
};
